import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { Escala, Turno } from '@core/models/models';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';

const DESCANSO_FOLGA_MS = 48 * 60 * 60 * 1000;
const DIA_MS = 24 * 60 * 60 * 1000;
const CATEGORIA_OPERATIVO_ID = 2;

export interface EfetivoDoPosto {
  id: number;
  nome: string;
  codigo?: string;
  cargo?: string;
  urlFoto?: string;
  idCategoria?: number;
  nomeCategoria?: string;
  turnoOriginal?: any;
}

export interface FolgaCelula {
  tipo: 'inicio' | 'continuidade';
  inicio: Date;
  fim: Date;
}

export interface EscalaMembroCalendario {
  efetivoId: number;
  escalaEfetivoId?: number;
  nome: string;
  codigo?: string;
  cargo?: string;
  urlFoto?: string;
  papel?: string | number;
  isFalta?: boolean;
  faltaId?: number;
  raw?: any;
}

export type CategoriaAssiduidade =
  | 'horario'
  | 'efectividade'
  | 'assiduidade'
  | 'ausencia';

export interface EscalaSlotEvent {
  efetivo: EfetivoDoPosto;
  data: Date;
  escala: Escala | null;
  escalas: Escala[];
  membros: EscalaMembroCalendario[];
  cell?: CelulaEscala;
  categoria?: CategoriaAssiduidade;
}

type TipoTurno = 'D' | 'N' | '24' | 'R' | 'F';
type EstadoOperacional =
  | 'planeado'
  | 'presente'
  | 'atraso'
  | 'falta'
  | 'concluido'
  | 'folga'
  | 'cancelado';

interface DiaSemana {
  data: Date;
  isoDate: string;
  dia: string;
  numero: number;
  hoje: boolean;
}

export interface CelulaEscala {
  data: Date;
  escala: Escala | null;
  escalas: Escala[];
  folga?: FolgaCelula | null;
  exibirFolgaNoLugarDaEscala?: boolean;
  tipo: TipoTurno | null;
  nomeTurno: string;
  horario: string;
  estado: string;
  conflitos: number;
  hoje: boolean;
  membros: EscalaMembroCalendario[];
  papel: string;
  membroAtual: EscalaMembroCalendario | null;
  estadoOperacional: EstadoOperacional;
  estadoLabel: string;
  horaEntrada: string;
  horaSaida: string;
  motivoFalta: string;
  iconeTurno: string;
}

interface LinhaCalendario {
  efetivo: EfetivoDoPosto;
  iniciais: string;
  cells: CelulaEscala[];
}

@Component({
  selector: 'app-escala-calendario',
  standalone: true,
  imports: [CommonModule, SkeletonModule, TooltipModule],
  templateUrl: './escala-calendario.component.html',
  styleUrl: './escala-calendario.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EscalaCalendarioComponent implements OnInit, OnChanges, OnDestroy {
  @Input() efetivos: EfetivoDoPosto[] = [];
  @Input() escalas: Escala[] = [];
  @Input() turnos: Turno[] = [];
  @Input() semanaInicio = this.getMonday(new Date());
  @Input() loading = false;
  @Input('readonly') isReadonly = false;
  @Input() contexto: 'posto' | 'efetivo' = 'posto';
  @Input() selectedEscalaId: number | null = null;
  @Input() selectedDate: Date | null = null;
  @Input() selectedCategory: CategoriaAssiduidade | null = null;

  @Output() weekChange = new EventEmitter<Date>();
  @Output() slotClick = new EventEmitter<EscalaSlotEvent>();
  @Output() removeClick = new EventEmitter<EscalaSlotEvent>();
  @Output() absenceClick = new EventEmitter<EscalaSlotEvent>();
  @Output() conflictClick = new EventEmitter<EscalaSlotEvent>();

  readonly skeletonRows = [1, 2, 3, 4, 5];
  readonly labels = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab', 'Dom'];
  readonly folgaEstadoOperacional: EstadoOperacional = 'folga';

  diasSemana: DiaSemana[] = [];
  calendarRows: LinhaCalendario[] = [];

  private readonly cdr = inject(ChangeDetectorRef);
  private updateTimer?: ReturnType<typeof setInterval>;

  get isCurrentWeek(): boolean {
    return (
      this.getMonday(this.semanaInicio).getTime() ===
      this.getMonday(new Date()).getTime()
    );
  }

  get totalTurnosPlaneados(): number {
    return this.countCells((cell) => !!cell.escala);
  }

  get totalPresencas(): number {
    return this.countCells(
      (cell) =>
        cell.estadoOperacional === 'presente' ||
        cell.estadoOperacional === 'concluido',
    );
  }

  get totalAtrasos(): number {
    return this.countCells((cell) => cell.estadoOperacional === 'atraso');
  }

  get totalFaltas(): number {
    return this.countCells((cell) => cell.estadoOperacional === 'falta');
  }

  ngOnInit(): void {
    this.buildCalendar();
    this.startUpdateTimer();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['efetivos'] ||
      changes['escalas'] ||
      changes['turnos'] ||
      changes['semanaInicio']
    ) {
      this.buildCalendar();
    }
  }

  ngOnDestroy(): void {
    this.stopUpdateTimer();
  }

  private startUpdateTimer(): void {
    this.stopUpdateTimer();
    this.updateTimer = setInterval(() => {
      this.buildCalendar();
      this.cdr.markForCheck();
    }, 60_000);
  }

  private stopUpdateTimer(): void {
    if (this.updateTimer) {
      clearInterval(this.updateTimer);
      this.updateTimer = undefined;
    }
  }

  getCellAriaLabel(row: LinhaCalendario, cell: CelulaEscala): string {
    const dia = new Intl.DateTimeFormat('pt-AO', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
    }).format(cell.data);
    return `${row.efetivo.nome}, ${dia}: ${this.getCellTooltip(cell)}`;
  }

  private countCells(predicate: (cell: CelulaEscala) => boolean): number {
    return this.calendarRows.reduce(
      (total, row) => total + row.cells.filter(predicate).length,
      0,
    );
  }

  semanaAnterior(): void {
    this.emitWeek(-7);
  }

  semanaSeguinte(): void {
    this.emitWeek(7);
  }

  irParaHoje(): void {
    this.weekChange.emit(this.getMonday(new Date()));
  }

  abrirSlot(
    row: LinhaCalendario,
    cell: CelulaEscala,
    categoria?: CategoriaAssiduidade,
  ): void {
    if (this.isReadonly && this.contexto !== 'efetivo') return;

    if (cell.estadoOperacional === 'falta' && this.contexto !== 'efetivo') {
      this.emitirFalta(row, cell);
      return;
    }

    this.slotClick.emit({
      efetivo: row.efetivo,
      data: new Date(cell.data),
      escala: cell.escala,
      escalas: cell.escalas,
      membros: cell.membros,
      cell,
      categoria,
    });
  }

  isRemocaoBloqueada(cell: CelulaEscala): boolean {
    return (
      cell.estadoOperacional === 'presente' ||
      cell.estadoOperacional === 'atraso' ||
      cell.estadoOperacional === 'concluido'
    );
  }

  getRemocaoTooltip(cell: CelulaEscala): string {
    if (cell.estadoOperacional === 'concluido') {
      return 'Não é possível eliminar uma escala concluída';
    }
    if (
      cell.estadoOperacional === 'presente' ||
      cell.estadoOperacional === 'atraso'
    ) {
      return 'Não é possível eliminar este turno porque já está em curso.';
    }
    return 'Remover deste dia';
  }

  abrirRemocao(event: Event, row: LinhaCalendario, cell: CelulaEscala): void {
    event.stopPropagation();
    if (!cell.escala || this.isReadonly || this.isRemocaoBloqueada(cell))
      return;

    this.removeClick.emit({
      efetivo: row.efetivo,
      data: new Date(cell.data),
      escala: cell.escala,
      escalas: cell.escalas,
      membros: cell.membros,
    });
  }

  abrirFalta(event: Event, row: LinhaCalendario, cell: CelulaEscala): void {
    event.stopPropagation();
    if (
      !cell.escala ||
      this.isReadonly ||
      cell.estadoOperacional === 'presente' ||
      cell.estadoOperacional === 'atraso'
    )
      return;

    this.emitirFalta(row, cell);
  }

  private emitirFalta(row: LinhaCalendario, cell: CelulaEscala): void {
    this.absenceClick.emit({
      efetivo: row.efetivo,
      data: new Date(cell.data),
      escala: cell.escala,
      escalas: cell.escalas,
      membros: cell.membros,
    });
  }

  abrirEdicao(event: Event, row: LinhaCalendario, cell: CelulaEscala): void {
    event.stopPropagation();
    if (
      !cell.escala ||
      this.isReadonly ||
      cell.estadoOperacional === 'falta' ||
      cell.estadoOperacional === 'presente' ||
      cell.estadoOperacional === 'atraso'
    )
      return;

    this.slotClick.emit({
      efetivo: row.efetivo,
      data: new Date(cell.data),
      escala: cell.escala,
      escalas: cell.escalas,
      membros: cell.membros,
      cell,
    });
  }

  isCellSelected(
    cell: CelulaEscala,
    categoria?: CategoriaAssiduidade,
  ): boolean {
    if (!cell) return false;
    let isSelectedCell = false;
    if (this.selectedEscalaId && cell.escala) {
      const id =
        (cell.escala as any).id ??
        (cell.escala as any).idEscala ??
        (cell.escala as any).escalaId;
      if (Number(id) === Number(this.selectedEscalaId)) {
        if (this.selectedDate && cell.data) {
          isSelectedCell = this.sameDay(
            new Date(cell.data),
            new Date(this.selectedDate),
          );
        } else {
          isSelectedCell = true;
        }
      }
    } else if (this.selectedDate && cell.data) {
      isSelectedCell = this.sameDay(
        new Date(cell.data),
        new Date(this.selectedDate),
      );
    }

    if (!isSelectedCell) return false;
    if (categoria && this.selectedCategory) {
      return this.selectedCategory === categoria;
    }
    return isSelectedCell;
  }

  isDaySelected(dia: DiaSemana): boolean {
    if (!this.selectedDate || !dia?.data) return false;
    return this.sameDay(new Date(dia.data), new Date(this.selectedDate));
  }

  abrirConflitos(event: Event, row: LinhaCalendario, cell: CelulaEscala): void {
    event.stopPropagation();
    if (cell.conflitos < 2) return;
    this.conflictClick.emit({
      efetivo: row.efetivo,
      data: new Date(cell.data),
      escala: cell.escala,
      escalas: cell.escalas,
      membros: cell.membros,
    });
  }

  getSemanaLabel(): string {
    const inicio = this.getMonday(this.semanaInicio);
    const fim = this.addDays(inicio, 6);
    const mesmoMes =
      inicio.getMonth() === fim.getMonth() &&
      inicio.getFullYear() === fim.getFullYear();

    if (mesmoMes) {
      const mesAno = fim.toLocaleDateString('pt-AO', {
        month: 'long',
        year: 'numeric',
      });
      return `${inicio.getDate()} a ${fim.getDate()} de ${mesAno}`;
    }

    const format = (date: Date) =>
      date.toLocaleDateString('pt-AO', { day: '2-digit', month: 'short' });

    return `${format(inicio)} a ${format(fim)} de ${fim.getFullYear()}`;
  }

  getCellTooltip(cell: CelulaEscala): string {
    if (cell.folga) {
      return cell.folga.tipo === 'inicio'
        ? `Folga - início: ${this.getFolgaInicio(cell)}`
        : `Folga - termina ${this.getFolgaTermino(cell)}`;
    }

    if (!cell.escala) {
      return this.isReadonly
        ? 'Sem escala neste dia'
        : 'Sem escala - clique para criar';
    }

    const membros =
      cell.membros.length > 1 ? ` - ${cell.membros.length} efetivos` : '';
    const papel = cell.papel ? ` - ${cell.papel}` : '';
    const conflito =
      cell.conflitos > 1 ? ` - ${cell.conflitos} escalas sobrepostas` : '';

    let action = '';
    if (!this.isReadonly) {
      if (cell.estadoOperacional === 'falta') {
        action =
          ' - edição bloqueada por falta registada; clique para ver os detalhes';
      } else if (
        cell.estadoOperacional === 'presente' ||
        cell.estadoOperacional === 'atraso'
      ) {
        action = ' - efetivo em serviço (ações bloqueadas)';
      } else if (cell.estadoOperacional === 'concluido') {
        action = ' - turno concluído (remoção bloqueada)';
      } else {
        action = ' - clique para ver historico, editar ou marcar falta';
      }
    }

    return `${cell.nomeTurno} - ${cell.horario} - ${cell.estado}${papel}${membros}${conflito}${action}`;
  }

  getTipoClass(tipo: TipoTurno | null): string {
    return tipo ? `turno-${tipo.toLowerCase()}` : '';
  }

  getEstadoClass(estado: string): string {
    const normalized = this.normalizeText(estado);
    if (normalized.includes('CONCL')) return 'estado-concluida';
    if (normalized.includes('EXEC')) return 'estado-execucao';
    if (normalized.includes('CANCEL')) return 'estado-cancelada';
    if (normalized.includes('AUS') || normalized.includes('FALTA')) {
      return 'estado-falta';
    }
    return 'estado-agendada';
  }

  getEstadoOperacionalClass(estado: EstadoOperacional): string {
    return `operacao-${estado}`;
  }

  getEstadoIcon(estado: EstadoOperacional): string {
    const icons: Record<EstadoOperacional, string> = {
      planeado: 'ri-calendar-check-line',
      presente: 'ri-login-circle-line',
      atraso: 'ri-alarm-warning-line',
      falta: 'ri-user-unfollow-line',
      concluido: 'ri-checkbox-circle-line',
      folga: 'ri-moon-clear-line',
      cancelado: 'ri-close-circle-line',
    };
    return icons[estado];
  }

  getTurnoIcon(tipo: TipoTurno | null): string {
    const icons: Record<TipoTurno, string> = {
      D: 'ri-sun-line',
      N: 'ri-moon-clear-line',
      '24': 'ri-24-hours-line',
      R: 'ri-shield-user-line',
      F: 'ri-rest-time-line',
    };
    return tipo ? icons[tipo] : 'ri-time-line';
  }

  getHorarioTurnoIcon(cell: CelulaEscala): string {
    return cell.iconeTurno || this.getTurnoIcon(cell.tipo);
  }

  getAssiduidadeHorario(cell: CelulaEscala): string {
    if (!cell.horaEntrada || cell.estadoOperacional === 'falta') return '';
    const inicioPrevisto = cell.horario.split(' - ')[0]?.trim();
    if (!inicioPrevisto) return '';
    return `${inicioPrevisto}–${cell.horaEntrada}`;
  }

  getEfectividadeHorario(cell: CelulaEscala): string {
    if (!cell.horaEntrada || cell.estadoOperacional === 'falta') return '';
    return cell.horaSaida
      ? `${cell.horaEntrada}–${cell.horaSaida}`
      : `${cell.horaEntrada}–…`;
  }

  getEfectividadeLabel(cell: CelulaEscala): string {
    if (cell.estadoOperacional === 'falta') return 'Sem entrada';
    if (cell.horaEntrada) return this.getEfectividadeHorario(cell);
    return this.isPastDay(cell.data)
      ? 'Entrada não registada'
      : 'Turno não iniciado';
  }

  getEfectividadeDescricao(cell: CelulaEscala): string {
    if (cell.estadoOperacional === 'falta')
      return 'Ausência registada para este turno';
    if (cell.horaEntrada && cell.horaSaida)
      return 'Entrada e saída confirmadas';
    if (cell.horaEntrada) return 'Em serviço · saída ainda pendente';
    return this.isPastDay(cell.data)
      ? 'O turno terminou sem movimento de entrada'
      : 'Aguarda o registo de entrada do efetivo';
  }

  getAssiduidadeLabel(cell: CelulaEscala): string {
    if (cell.estadoOperacional === 'falta') return 'Não processada';
    if (cell.horaEntrada) return this.getAssiduidadeHorario(cell);
    return this.isPastDay(cell.data)
      ? 'Assiduidade não processada'
      : 'Assiduidade pendente';
  }

  getAssiduidadeDescricao(cell: CelulaEscala): string {
    if (cell.estadoOperacional === 'falta')
      return 'Não existe presença para processar';
    if (cell.horaEntrada) {
      return cell.estadoOperacional === 'atraso'
        ? 'Entrada realizada depois do horário previsto'
        : 'Comparação entre horário previsto e entrada real';
    }
    return this.isPastDay(cell.data)
      ? 'Não foi possível calcular sem uma entrada real'
      : 'Será calculada depois do registo de entrada';
  }

  getAusenciaLabel(cell: CelulaEscala): string {
    return cell.estadoOperacional === 'falta'
      ? 'Falta registada'
      : 'Nenhuma falta';
  }

  getAusenciaDescricao(cell: CelulaEscala): string {
    return cell.estadoOperacional === 'falta'
      ? cell.motivoFalta || 'O efetivo foi marcado como ausente'
      : 'Não existe ausência associada a este turno';
  }

  trackEfetivo(_: number, row: LinhaCalendario): number {
    return Number(row.efetivo.id);
  }

  trackDia(_: number, cell: CelulaEscala): number {
    return cell.data.getTime();
  }

  private buildCalendar(): void {
    if (!this.efetivos || !Array.isArray(this.efetivos)) {
      this.calendarRows = [];
      return;
    }

    const hoje = this.startOfDay(new Date());
    const semanaInicio = this.getMonday(this.semanaInicio);

    this.diasSemana = this.labels.map((dia, index) => {
      const data = this.addDays(semanaInicio, index);
      return {
        data,
        isoDate: this.toDateKey(data),
        dia,
        numero: data.getDate(),
        hoje: this.sameDay(data, hoje),
      };
    });

    // Mapeia TODOS os efetivos alocados independentemente de terem escalas no período visível
    this.calendarRows = this.efetivos.map((efetivo) => ({
      efetivo,
      iniciais: this.getInitials(efetivo.nome),
      cells: this.diasSemana.map((dia) =>
        this.buildCell(Number(efetivo.id), dia.data, dia.hoje),
      ),
    }));
  }

  private buildCell(idEfetivo: number, dia: Date, hoje: boolean): CelulaEscala {
    const encontradas = (this.escalas || [])
      .filter(
        (escala) =>
          this.escalaPertenceAoEfetivo(escala, idEfetivo) &&
          this.escalaAbrangeDia(escala, dia),
      )
      .sort((a, b) => {
        const aDate = this.getEscalaInicio(a)?.getTime() ?? 0;
        const bDate = this.getEscalaInicio(b)?.getTime() ?? 0;
        return aDate - bDate;
      });

    const escala =
      encontradas.find((item) =>
        this.escalaTemFaltaDoEfetivo(item, idEfetivo),
      ) ??
      encontradas[0] ??
      null;
    const turno = escala ? this.findTurno(escala) : null;
    const membros = escala ? this.getMembrosEscala(escala) : [];
    const membroAtual =
      membros.find((membro) => Number(membro.efetivoId) === Number(idEfetivo)) ?? null;
    const nomeTurno = escala
      ? String(
          (escala as any).nomeTurno ??
            (turno as any)?.nome ??
            (turno as any)?.designacao ??
            'Turno',
        )
      : '';
    const folgaCalculada = this.calcularFolga(idEfetivo, dia);
    const operacao = escala
      ? this.getOperacao(escala, membroAtual, turno, nomeTurno)
      : folgaCalculada
        ? {
            estado: 'folga' as EstadoOperacional,
            label: 'Folga',
            entrada: '',
            saida: '',
            motivo: '',
          }
        : {
            estado: 'planeado' as EstadoOperacional,
            label: 'Sem escala',
            entrada: '',
            saida: '',
            motivo: '',
          };

    // Substituicao exclusivamente visual: quando a folga de 48h comeca no dia em
    // que termina a escala, o cartao da escala (Entrada/Planeado) e apresentado
    // como primeiro cartao de Folga. A escala original permanece intacta.
    const escalaFim = escala ? this.getEscalaFimParaFolga(escala) : null;
    const agora = new Date();
    const canShowFolgaSubstitution =
      !!escalaFim &&
      (this.isPastDay(dia) || escalaFim.getTime() <= agora.getTime());
    const exibirFolgaNoLugarDaEscala =
      !!escala &&
      !!folgaCalculada &&
      folgaCalculada.tipo === 'inicio' &&
      this.folgaSubstituiCartao(operacao.estado) &&
      this.escalaTerminaNaFolga(escala, folgaCalculada) &&
      canShowFolgaSubstitution;

    const folga =
      !escala || exibirFolgaNoLugarDaEscala ? folgaCalculada : null;
    const inicioPrevisto = escala ? this.getEscalaInicio(escala) : null;
    const escalaAindaFutura = !!inicioPrevisto && Date.now() < inicioPrevisto.getTime();

    return {
      data: dia,
      escala,
      escalas: encontradas,
      folga,
      exibirFolgaNoLugarDaEscala,
      tipo: escala ? this.getTipoTurno(nomeTurno, escala) : null,
      nomeTurno,
      horario: escala ? this.getHorario(escala, turno) : '',
      estado: escala
        ? escalaAindaFutura && operacao.estado === 'planeado'
          ? 'Agendada'
          : String(
            (escala as any).estado ??
              (escala as any).estadoEscala ??
              'Agendada',
          )
        : '',
      conflitos: encontradas.length,
      hoje,
      membros,
      papel: membroAtual?.papel ? String(membroAtual.papel) : '',
      membroAtual,
      estadoOperacional: operacao.estado,
      estadoLabel: operacao.label,
      horaEntrada: operacao.entrada,
      horaSaida: operacao.saida,
      motivoFalta: operacao.motivo,
      iconeTurno: escala
        ? String((escala as any).iconTurno ?? (turno as any)?.iconTurno ?? '')
        : '',
    };
  }

  private getOperacao(
    escala: Escala,
    membro: EscalaMembroCalendario | null,
    turno: Turno | null,
    nomeTurno: string,
  ): {
    estado: EstadoOperacional;
    label: string;
    entrada: string;
    saida: string;
    motivo: string;
  } {
    const value = escala as any;
    const member = membro?.raw ?? {};
    const estadoRaw = this.normalizeText(
      member.estadoPresenca ??
        member.estado ??
        value.estadoPresenca ??
        value.estadoEscala ??
        value.estado,
    );
    const entradaRaw =
      member.horaInicioReal ??
      member.dataCheckIn ??
      member.horaCheckIn ??
      member.checkIn ??
      value.horaInicioReal ??
      value.dataCheckIn;
    const saidaRaw =
      member.horaFimReal ??
      member.dataCheckOut ??
      member.horaCheckOut ??
      member.checkOut ??
      value.horaFimReal ??
      value.dataCheckOut;
    const entrada = this.formatApiTime(entradaRaw);
    const saida = this.formatApiTime(saidaRaw);
    const motivo = String(
      member.motivoFalta ?? member.motivoAusencia ?? value.motivoFalta ?? '',
    );

    if (membro?.isFalta || this.readFaltaFlag(member)) {
      return {
        estado: 'falta',
        label: 'Falta',
        entrada: '',
        saida: '',
        motivo,
      };
    }
    if (estadoRaw.includes('FALTA') || estadoRaw.includes('AUSEN')) {
      return {
        estado: 'falta',
        label: 'Falta',
        entrada: '',
        saida: '',
        motivo,
      };
    }
    if (estadoRaw.includes('CANCEL')) {
      return {
        estado: 'cancelado',
        label: 'Cancelado',
        entrada,
        saida,
        motivo,
      };
    }
    if (this.getTipoTurno(nomeTurno, escala) === 'F') {
      return {
        estado: 'folga',
        label: 'Folga',
        entrada: '',
        saida: '',
        motivo: '',
      };
    }

    // Presenças ou estados desatualizados da API não podem antecipar o início
    // operacional de uma escala cuja data/hora prevista ainda está no futuro.
    const inicioPrevisto = this.getEscalaInicio(escala);
    if (inicioPrevisto && Date.now() < inicioPrevisto.getTime()) {
      return {
        estado: 'planeado',
        label: 'Planeado',
        entrada: '',
        saida: '',
        motivo,
      };
    }

    if (entrada) {
      const prevista = inicioPrevisto;
      const real = this.parseApiDate(entradaRaw);
      const atraso =
        !!prevista &&
        !!real &&
        real.getTime() - prevista.getTime() > 5 * 60_000;
      if (saida || estadoRaw.includes('CONCL')) {
        return {
          estado: 'concluido',
          label: 'Concluído',
          entrada,
          saida,
          motivo,
        };
      }
      return {
        estado: atraso ? 'atraso' : 'presente',
        label: atraso ? 'Entrada tardia' : 'Em serviço',
        entrada,
        saida,
        motivo,
      };
    }
    if (estadoRaw.includes('CONCL')) {
      return {
        estado: 'concluido',
        label: 'Concluído',
        entrada,
        saida,
        motivo,
      };
    }
    return {
      estado: 'planeado',
      label: 'Planeado',
      entrada: '',
      saida: '',
      motivo,
    };
  }

  private formatApiTime(value: unknown): string {
    if (!value) return '';
    const parsed = this.parseApiDate(value);
    if (parsed) return this.formatTime(parsed);
    return this.onlyTime(value) === '--:--' ? '' : this.onlyTime(value);
  }

  private escalaPertenceAoEfetivo(escala: Escala, idEfetivo: number): boolean {
    const value = escala as any;
    const idDireto = Number(
      value.idEfetivo ?? value.efetivoId ?? value.efetivo?.id,
    );

    if (idDireto === Number(idEfetivo)) return true;

    return this.getMembrosEscala(escala).some(
      (membro) => Number(membro.efetivoId) === Number(idEfetivo),
    );
  }

  private escalaTemFaltaDoEfetivo(escala: Escala, efetivoId: number): boolean {
    return this.getMembrosEscala(escala).some(
      (membro) => Number(membro.efetivoId) === Number(efetivoId) && membro.isFalta === true,
    );
  }

  private getMembrosEscala(escala: Escala): EscalaMembroCalendario[] {
    const value = escala as any;
    const source = this.getEscalaMembersSource(value);

    if (!source.length) {
      const efetivo = value.efetivo ?? {};
      const efetivoId = Number(
        value.idEfetivo ?? value.efetivoId ?? efetivo.id,
      );

      if (!efetivoId) return [];

      return [
        {
          efetivoId,
          escalaEfetivoId: this.toOptionalNumber(
            value.idEscalaEfetivo ??
              value.escalaEfetivoId ??
              value.efetivoEscalaId ??
              value.idEfetivoEscala,
          ),
          nome: String(
            value.nomeEfetivo ?? efetivo.nome ?? `Efetivo #${efetivoId}`,
          ),
          codigo: value.codigoEfetivo ?? efetivo.codigo,
          cargo: value.cargoEfetivo ?? efetivo.cargo,
          urlFoto: value.urlFoto ?? efetivo.urlFoto ?? efetivo.urlFotografia,
          papel:
            value.papelEfetivoEscalaEnum ??
            value.papel ??
            value.papelEfetivoEscala,
          isFalta: this.readFaltaFlag(value),
          faltaId: this.toOptionalNumber(value.faltaId ?? value.idFalta),
          raw: value,
        },
      ];
    }

    return source
      .map((item: any) => {
        const efetivo = item.efetivo ?? item;
        const efetivoId = Number(
          item.efetivoId ?? item.idEfetivo ?? efetivo.id,
        );
        if (!efetivoId) return null;

        return {
          efetivoId,
          escalaEfetivoId: this.toOptionalNumber(
            item.idEscalaEfetivo ??
              item.escalaEfetivoId ??
              item.efetivoEscalaId ??
              item.idEfetivoEscala,
          ),
          nome: String(
            item.nomeEfetivo ??
              efetivo.nome ??
              item.nome ??
              `Efetivo #${efetivoId}`,
          ),
          codigo: item.codigoEfetivo ?? efetivo.codigo ?? item.codigo,
          cargo: item.cargoEfetivo ?? efetivo.cargo ?? item.cargo,
          urlFoto: item.urlFoto ?? efetivo.urlFoto ?? efetivo.urlFotografia,
          papel:
            item.papelEfetivoEscalaEnum ??
            item.papel ??
            item.papelEfetivoEscala,
          isFalta: this.readFaltaFlag(item),
          faltaId: this.toOptionalNumber(item.faltaId ?? item.idFalta),
          raw: item,
        };
      })
      .filter(Boolean) as EscalaMembroCalendario[];
  }

  private getEscalaMembersSource(value: any): any[] {
    if (Array.isArray(value.efetivos)) return value.efetivos;
    if (Array.isArray(value.escalaEfetivos)) return value.escalaEfetivos;
    if (Array.isArray(value.efetivosEscala)) return value.efetivosEscala;
    if (Array.isArray(value.escalasEfetivos)) return value.escalasEfetivos;
    if (Array.isArray(value.membros)) return value.membros;
    return [];
  }

  private readFaltaFlag(value: any): boolean {
    return value?.isFalta === true || value?.hasFalta === true;
  }

  private escalaAbrangeDia(escala: Escala, dia: Date): boolean {
    const inicio = this.getEscalaInicio(escala);
    if (!inicio) return false;

    const diaNormalizado = this.startOfDay(dia);
    const inicioDia = this.startOfDay(inicio);
    const fim = this.getEscalaFim(escala);

    // Se não houver data de fim definida, compara diretamente se a escala é do mesmo dia
    if (!fim) {
      return this.sameDay(diaNormalizado, inicioDia);
    }

    const fimDia = this.startOfDay(fim);

    // Se o fim da escala for no mesmo dia do início ou se a duração for curta
    if (this.sameDay(inicioDia, fimDia)) {
      return this.sameDay(diaNormalizado, inicioDia);
    }

    // Suporte para escalas que abrangem vários dias ou cruzam a meia-noite
    return diaNormalizado >= inicioDia && diaNormalizado <= fimDia;
  }

  private turnoAtravessaMeiaNoite(escala: Escala): boolean {
    const turno = this.findTurno(escala);
    const [inicio, fim] = this.getHorario(escala, turno).split(' - ');
    return this.timeToMinutes(fim) <= this.timeToMinutes(inicio);
  }

  private findTurno(escala: Escala): Turno | null {
    const id = Number((escala as any).turnoId ?? (escala as any).idTurno ?? (escala as any).turno?.id);
    return (
      this.turnos.find((turno) => Number((turno as any).id) === id) ?? null
    );
  }

  private getHorario(escala: Escala, turno: Turno | null): string {
    const value = escala as any;
    const turnoValue = turno as any;
    const inicioData = this.getEscalaInicio(escala);
    const fimData = this.getEscalaFim(escala);
    const fallback = this.getFallbackHorario(
      value.nomeTurno ?? turnoValue?.nome,
    );

    const inicio =
      value.horaInicio ??
      turnoValue?.horaInicio ??
      turnoValue?.inicio ??
      (inicioData ? this.formatTime(inicioData) : fallback.inicio);
    const fim =
      value.horaFim ??
      value.horaTermino ??
      turnoValue?.horaFim ??
      turnoValue?.horaTermino ??
      turnoValue?.fim ??
      (fimData ? this.formatTime(fimData) : fallback.fim);

    return `${this.onlyTime(inicio)} - ${this.onlyTime(fim)}`;
  }

  private getTipoTurno(nomeTurno: string, escala: Escala): TipoTurno {
    const value = this.normalizeText(
      `${nomeTurno} ${(escala as any).cobertura ?? ''}`,
    );

    if (value.includes('FOLGA')) return 'F';
    if (value.includes('RESERVA')) return 'R';
    if (value.includes('24')) return '24';
    if (value.includes('NOTUR')) return 'N';
    return 'D';
  }

  private getFallbackHorario(nomeTurno?: string): {
    inicio: string;
    fim: string;
  } {
    const nome = this.normalizeText(nomeTurno);
    if (nome.includes('NOTUR') || nome.includes('NOITE')) return { inicio: '19:00', fim: '07:00' };
    if (nome.includes('24')) return { inicio: '08:30', fim: '08:30' };
    if (nome.includes('TARDE')) return { inicio: '14:00', fim: '22:00' };
    if (nome.includes('MANHA') || nome.includes('MANHÃ')) return { inicio: '06:00', fim: '14:00' };
    if (nome.includes('ADMINISTRATIV')) return { inicio: '08:00', fim: '17:00' };
    if (nome.includes('DIURN')) return { inicio: '07:00', fim: '19:00' };
    if (nome.includes('FOLGA')) return { inicio: '--:--', fim: '--:--' };
    return { inicio: '06:00', fim: '18:00' };
  }

  private getEscalaInicio(escala: Escala): Date | null {
    const value = escala as any;
    return this.parseApiDate(
      value.horaInicioReal ??
        value.dataInicioReal ??
        value.dataInicio ??
        value.dataInicioPrevista ??
        value.data,
    );
  }

  private getEscalaFim(escala: Escala): Date | null {
    const value = escala as any;
    return this.parseApiDate(
      value.horaFimReal ??
        value.dataTerminoReal ??
        value.dataFimReal ??
        value.dataTermino ??
        value.dataFim ??
        value.dataTerminoPrevista ??
        value.dataFimPrevista,
    );
  }

  private getEscalaFimParaFolga(escala: Escala): Date | null {
    if (!escala) return null;
    const value = escala as any;
    const temFimReal = !!(
      value.horaFimReal ||
      value.dataFimReal ||
      value.dataTerminoReal
    );

    if (temFimReal) {
      const fimDireto = this.getEscalaFim(escala);
      if (fimDireto) return fimDireto;
    }

    // Quando não existe fim real registado, derivar sempre pelo horário padrão do turno.
    return this.getEscalaFimPrevisto(escala);
  }

  private getEscalaFimPrevisto(escala: Escala): Date | null {
    const inicio = this.getEscalaInicio(escala);
    if (!inicio) return null;

    const value = escala as any;
    const turno = this.findTurno(escala) as any;
    const nomeTurno = String(
      value.nomeTurno ?? turno?.nome ?? turno?.designacao ?? '',
    );
    const nome = this.normalizeText(nomeTurno);

    let horaFimStr = '';
    if (nome.includes('ADMINISTRATIV')) {
      horaFimStr = '17:00';
    } else if (nome.includes('12') && nome.includes('NOTUR')) {
      horaFimStr = '07:00';
    } else if (nome.includes('12') && (nome.includes('DIURN') || nome.includes('DIURNO'))) {
      horaFimStr = '19:00';
    } else if (nome.includes('NOITE')) {
      horaFimStr = '06:00';
    } else if (nome.includes('TARDE')) {
      horaFimStr = '22:00';
    } else if (nome.includes('MANHA') || nome.includes('MANHÃ')) {
      horaFimStr = '14:00';
    } else if (nome.includes('24')) {
      horaFimStr = '08:30';
    } else {
      horaFimStr = String(
        value.horaFim ??
          value.horaTermino ??
          turno?.horaFim ??
          turno?.horaTermino ??
          turno?.fim ??
          '',
      );
    }

    const match = horaFimStr.match(/(\d{2}):(\d{2})/);
    if (!match) return null;

    const fim = new Date(inicio);
    fim.setHours(Number(match[1]), Number(match[2]), 0, 0);
    if (fim.getTime() <= inicio.getTime()) {
      fim.setDate(fim.getDate() + 1);
    }
    return fim;
  }

  private folgaSubstituiCartao(estado: EstadoOperacional): boolean {
    // "Entrada" (presenca registada) e "Planeado" sao substituidos pela folga.
    // Ausencias e cancelamentos mantem o cartao legitimo.
    return estado !== 'falta' && estado !== 'cancelado' && estado !== 'folga';
  }

  private escalaTerminaNaFolga(escala: Escala, folga: FolgaCelula): boolean {
    const fim = this.getEscalaFimParaFolga(escala);
    return !!fim && fim.getTime() === folga.inicio.getTime();
  }

  private calcularFolga(idEfetivo: number, dia: Date): FolgaCelula | null {
    if (this.contexto !== 'posto') return null;

    const efetivo = this.efetivos.find(
      (item) => Number(item.id) === Number(idEfetivo),
    );
    if (!efetivo || !this.isEfetivoOperativo(efetivo)) return null;

    const fimEscala = this.getFimEscalaAnterior(idEfetivo, dia);
    if (!fimEscala) return null;

    const inicio = new Date(fimEscala.getTime());
    const fim = new Date(fimEscala.getTime() + DESCANSO_FOLGA_MS);

    // Escalas que comecam e terminam no mesmo dia ocupam totalmente esse dia de
    // trabalho, pelo que o cartao real deve permanecer visivel. Nesse caso os
    // dois cartoes de folga sao colocados nos dias seguintes (ancora +1 dia),
    // mantendo intacto o intervalo real das 48h a partir do fim da escala.
    const ancora = this.escalaAnteriorTerminaNoMesmoDia(idEfetivo, fimEscala)
      ? this.addDays(this.startOfDay(inicio), 1)
      : this.startOfDay(inicio);
    const diffDias = Math.round(
      (this.startOfDay(dia).getTime() - ancora.getTime()) / DIA_MS,
    );

    if (diffDias === 0) return { tipo: 'inicio', inicio, fim };
    if (diffDias === 1) return { tipo: 'continuidade', inicio, fim };
    return null;
  }

  private escalaAnteriorTerminaNoMesmoDia(
    idEfetivo: number,
    fimEscala: Date,
  ): boolean {
    for (const escala of this.escalas) {
      if (
        !this.escalaPertenceAoEfetivo(escala, idEfetivo) ||
        this.isEscalaFolga(escala)
      ) {
        continue;
      }

      const fim = this.getEscalaFimParaFolga(escala);
      if (!fim || fim.getTime() !== fimEscala.getTime()) continue;

      const inicio = this.getEscalaInicio(escala);
      return (
        !!inicio &&
        this.startOfDay(inicio).getTime() === this.startOfDay(fim).getTime()
      );
    }

    return false;
  }

  private getFimEscalaAnterior(idEfetivo: number, dia: Date): Date | null {
    const limite = this.addDays(this.startOfDay(dia), 1).getTime();
    let maisRecente: Date | null = null;

    for (const escala of this.escalas) {
      if (
        !this.escalaPertenceAoEfetivo(escala, idEfetivo) ||
        this.isEscalaFolga(escala)
      ) {
        continue;
      }

      const fim = this.getEscalaFimParaFolga(escala);
      if (!fim || fim.getTime() >= limite) continue;
      if (!maisRecente || fim.getTime() > maisRecente.getTime()) {
        maisRecente = fim;
      }
    }

    return maisRecente;
  }

  private isEfetivoOperativo(efetivo: EfetivoDoPosto): boolean {
    const idCategoria = Number(efetivo.idCategoria);
    if (Number.isFinite(idCategoria) && idCategoria > 0) {
      return idCategoria === CATEGORIA_OPERATIVO_ID;
    }

    const nome = this.normalizeText(efetivo.nomeCategoria);
    if (!nome) return false;
    if (nome.includes('ESTRATEGIC') || nome.includes('ADMINISTRATIV')) {
      return false;
    }
    return nome.includes('OPERATIV') || nome.includes('OPERACION');
  }

  private isEscalaFolga(escala: Escala): boolean {
    const value = escala as any;
    const turno = this.findTurno(escala) as any;
    const nomeTurno = String(
      value.nomeTurno ?? turno?.nome ?? turno?.designacao ?? '',
    );
    return this.getTipoTurno(nomeTurno, escala) === 'F';
  }

  getFolgaInicio(cell: CelulaEscala): string {
    return cell.folga ? this.formatFolgaHora(cell.folga.inicio) : '';
  }

  getFolgaTermino(cell: CelulaEscala): string {
    return cell.folga ? this.formatFolgaDiaHora(cell.folga.fim) : '';
  }

  getFolgaTerminoDia(cell: CelulaEscala): string {
    return cell.folga ? this.formatFolgaDia(cell.folga.fim) : '';
  }

  getFolgaTerminoHora(cell: CelulaEscala): string {
    return cell.folga ? this.formatFolgaHora(cell.folga.fim) : '';
  }

  private formatFolgaHora(date: Date): string {
    const horas = String(date.getHours()).padStart(2, '0');
    const minutos = String(date.getMinutes()).padStart(2, '0');
    return `${horas}h${minutos}`;
  }

  private formatFolgaDia(date: Date): string {
    return new Intl.DateTimeFormat('pt-AO', {
      weekday: 'long',
    }).format(date);
  }

  private formatFolgaDiaHora(date: Date): string {
    return `${this.formatFolgaDia(date)}, às ${this.formatFolgaHora(date)}`;
  }

  private emitWeek(offset: number): void {
    this.weekChange.emit(this.addDays(this.semanaInicio, offset));
  }

  private parseApiDate(value: unknown): Date | null {
    if (!value) return null;
    const date =
      value instanceof Date ? new Date(value) : new Date(String(value));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private formatTime(date: Date): string {
    return `${String(date.getHours()).padStart(2, '0')}:${String(
      date.getMinutes(),
    ).padStart(2, '0')}`;
  }

  private onlyTime(value: unknown): string {
    const text = String(value ?? '');
    const match = text.match(/(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : text || '--:--';
  }

  private timeToMinutes(time: string): number {
    const match = String(time).match(/(\d{2}):(\d{2})/);
    return match ? Number(match[1]) * 60 + Number(match[2]) : 0;
  }

  private toOptionalNumber(value: unknown): number | undefined {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }

  private normalizeText(value: unknown): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toUpperCase();
  }

  private getInitials(nome: string): string {
    const parts = String(nome ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!parts.length) return '?';

    return parts.length > 1
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
  }

  private getMonday(date: Date): Date {
    const result = this.startOfDay(date);
    const day = result.getDay();
    result.setDate(result.getDate() + (day === 0 ? -6 : 1 - day));
    return result;
  }

  private startOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
  }

  private addDays(date: Date, amount: number): Date {
    const result = this.startOfDay(date);
    result.setDate(result.getDate() + amount);
    return result;
  }

  private sameDay(first: Date, second: Date): boolean {
    return this.toDateKey(first) === this.toDateKey(second);
  }

  private isPastDay(date: Date): boolean {
    return (
      this.startOfDay(date).getTime() < this.startOfDay(new Date()).getTime()
    );
  }

  private toDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
